import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import {
  ShieldCheck, Gauge, FileWarning, Loader2, TrendingUp,
  CheckCircle2, AlertTriangle, XCircle, PlusCircle, Settings,
  Bell, Play, Trash2, ToggleLeft, ToggleRight, Search,
  ThumbsUp, ThumbsDown, Pen, ChevronRight, Eye
} from "lucide-react";

const SEVERITY_COLORS = {
  critical: "bg-red-500/20 text-red-400 border-red-500/30",
  high: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  medium: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  low: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
};

const METRIC_OPTIONS = [
  { value: "major_findings_count", label: "Major/Critical Findings" },
  { value: "total_findings_count", label: "Total Findings" },
  { value: "backroom_pending_count", label: "Back Room Pending" },
  { value: "checklist_completion_pct", label: "Checklist Completion %" },
  { value: "concern_postures_count", label: "Concern Postures" },
  { value: "repeat_clause_findings", label: "Repeat Clause Findings" },
  { value: "readiness_score", label: "Readiness Score" },
];

const SIGNAL_TYPE_LABELS = {
  repeat_clause_failure: "Repeat Clause Failure",
  repeat_supplier_issue: "Repeat Supplier Issue",
  weak_capa_effectiveness: "Weak CAPA Effectiveness",
  containment_only_warning: "Containment-Only Warning",
  repeated_evidence_gap: "Repeated Evidence Gap",
};

const CAPAPreventionPage = () => {
  const { t } = useTranslation('capa');
  const [tab, setTab] = useState("dashboard");
  const [score, setScore] = useState(null);
  const [rules, setRules] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [signals, setSignals] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [showNewRule, setShowNewRule] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState({});
  const [newRule, setNewRule] = useState({
    name: "", description: "", metric: "major_findings_count",
    operator: ">=", threshold: 3, severity: "medium", scope: "per_audit", action: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [rRes, aRes, sRes, recRes] = await Promise.all([
        fetch(`${API}/auditiq/capa-prevention/rules`, { credentials: "include" }),
        fetch(`${API}/auditiq/capa-prevention/alerts`, { credentials: "include" }),
        fetch(`${API}/auditiq/capa-prevention/recurrence-signals`, { credentials: "include" }),
        fetch(`${API}/auditiq/capa-prevention/recommendations`, { credentials: "include" }),
      ]);
      if (rRes.ok) setRules((await rRes.json()).rules || []);
      if (aRes.ok) setAlerts((await aRes.json()).alerts || []);
      if (sRes.ok) setSignals((await sRes.json()).signals || []);
      if (recRes.ok) setRecommendations((await recRes.json()).recommendations || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const calcScore = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/auditiq/capa-prevention/readiness-score`, { credentials: "include" });
      if (res.ok) { setScore(await res.json()); fetchData(); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const evaluateRules = async () => {
    setEvaluating(true);
    try {
      const res = await fetch(`${API}/auditiq/capa-prevention/evaluate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({}),
      });
      if (res.ok) fetchData();
    } catch (e) { console.error(e); }
    setEvaluating(false);
  };

  const analyzeRecurrence = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/auditiq/capa-prevention/analyze-recurrence`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({}),
      });
      if (res.ok) fetchData();
    } catch (e) { console.error(e); }
    setAnalyzing(false);
  };

  const createRule = async () => {
    try {
      await fetch(`${API}/auditiq/capa-prevention/rules`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(newRule),
      });
      setShowNewRule(false);
      setNewRule({ name: "", description: "", metric: "major_findings_count", operator: ">=", threshold: 3, severity: "medium", scope: "per_audit", action: "" });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const toggleRule = async (ruleId, active) => {
    try {
      await fetch(`${API}/auditiq/capa-prevention/rules/${ruleId}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ active: !active }),
      });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const deleteRule = async (ruleId) => {
    try {
      await fetch(`${API}/auditiq/capa-prevention/rules/${ruleId}`, { method: "DELETE", credentials: "include" });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const decideRecommendation = async (recId, decision, notes = "") => {
    try {
      await fetch(`${API}/auditiq/capa-prevention/recommendations/${recId}/decide`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ decision, notes }),
      });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const sendFeedback = async (targetId, rating) => {
    try {
      await fetch(`${API}/auditiq/capa-prevention/feedback`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ target_id: targetId, rating }),
      });
      setFeedbackSent(p => ({ ...p, [targetId]: rating }));
    } catch (e) { console.error(e); }
  };

  const riskColor = { low: "text-emerald-400", medium: "text-yellow-400", high: "text-red-400" };

  return (
    <QMSLayout title={t('prevention.title')}>
      <div className="space-y-4" data-testid="capa-prevention-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>CAPA Prevention Intelligence</h1>
            <p className="text-slate-400 text-xs mt-1">Threshold rules, recurrence detection, and prevention recommendations</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={analyzeRecurrence} disabled={analyzing} data-testid="analyze-recurrence-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500 text-white text-xs font-bold uppercase tracking-wide rounded-[3px] disabled:opacity-50">
              {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              {t('prevention.analyze')}
            </button>
            <button onClick={evaluateRules} disabled={evaluating} data-testid="evaluate-rules-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-black text-xs font-bold uppercase tracking-wide rounded-[3px] disabled:opacity-50">
              {evaluating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
              Run Rules
            </button>
            <button onClick={calcScore} disabled={loading} data-testid="calc-readiness-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1A6BCC] text-white text-xs font-bold uppercase tracking-wide rounded-[3px] disabled:opacity-50">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Gauge className="w-3.5 h-3.5" />}
              Readiness
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#0F1E35] p-1 rounded-lg w-fit flex-wrap">
          {[
            { id: "dashboard", label: "Dashboard", icon: ShieldCheck },
            { id: "signals", label: `Signals (${signals.length})`, icon: AlertTriangle },
            { id: "recommendations", label: `Suggestions (${recommendations.length})`, icon: FileWarning },
            { id: "rules", label: `Rules (${rules.length})`, icon: Settings },
            { id: "alerts", label: `Alerts (${alerts.length})`, icon: Bell },
          ].map(tabDef => (
            <button key={tabDef.id} onClick={() => setTab(tabDef.id)} data-testid={`tab-${tabDef.id}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === tabDef.id ? "bg-[#1A6BCC] text-white" : "text-[#94A3B8] hover:text-white"}`}>
              <tabDef.icon className="w-3.5 h-3.5" /> {tabDef.label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {tab === "dashboard" && (
          <div className="space-y-4">
            {/* Quick stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Active Signals", value: signals.filter(s => s.status === "active").length, color: "text-purple-400" },
                { label: "Pending Suggestions", value: recommendations.filter(r => r.status === "pending").length, color: "text-amber-400" },
                { label: "Active Rules", value: rules.filter(r => r.active).length, color: "text-cyan-400" },
                { label: "Recent Alerts", value: alerts.length, color: "text-red-400" },
              ].map((stat, i) => (
                <div key={i} className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-3">
                  <span className="text-[10px] text-slate-500 uppercase">{stat.label}</span>
                  <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
                </div>
              ))}
            </div>

            {score && (
              <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-4" data-testid="readiness-score-panel">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`text-3xl font-bold ${riskColor[score.risk_level]}`}>{score.overall_score}%</div>
                  <div className={`text-xs font-bold uppercase ${riskColor[score.risk_level]}`}>Risk: {score.risk_level}</div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(score.categories || {}).map(([key, cat]) => (
                    <div key={key} className="p-2 bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px]">
                      <span className="text-slate-400 text-[10px] capitalize">{key.replace(/_/g, " ")}</span>
                      <div className={`text-lg font-bold ${cat.score >= 80 ? "text-emerald-400" : cat.score >= 60 ? "text-yellow-400" : "text-red-400"}`}>{cat.score}%</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Signals Tab */}
        {tab === "signals" && (
          <div className="space-y-3" data-testid="signals-tab">
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#94A3B8]">{signals.length} recurrence signals detected</span>
              <button onClick={analyzeRecurrence} disabled={analyzing}
                className="flex items-center gap-1 px-3 py-1.5 bg-purple-500 text-white text-xs font-bold rounded-[3px] disabled:opacity-50">
                {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Re-analyze
              </button>
            </div>
            {signals.length === 0 ? (
              <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-emerald-500/30 mx-auto mb-3" />
                <p className="text-[#94A3B8] text-sm">{t('prevention.noSignals')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {signals.map((sig, i) => (
                  <div key={sig.signal_id || i} data-testid={`signal-${i}`}
                    className="bg-[#0F1E35] border border-[#1E3A5F] rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${SEVERITY_COLORS[sig.severity]}`}>{sig.severity}</span>
                        <span className="text-xs font-bold text-white">{SIGNAL_TYPE_LABELS[sig.signal_type] || sig.signal_type}</span>
                      </div>
                      <span className="text-[10px] text-[#94A3B8]">{sig.occurrence_count}x</span>
                    </div>
                    <p className="text-xs text-[#94A3B8] mb-1">{sig.explanation}</p>
                    <div className="flex flex-wrap gap-1.5 text-[10px]">
                      {sig.affected_audits?.map(a => <span key={a} className="px-1.5 py-0.5 bg-[#132340] text-cyan-400 rounded">{a}</span>)}
                      {sig.affected_clauses?.map(c => <span key={c} className="px-1.5 py-0.5 bg-amber-500/10 text-amber-400 rounded">Cl.{c}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recommendations Tab */}
        {tab === "recommendations" && (
          <div className="space-y-3" data-testid="recommendations-tab">
            <span className="text-xs text-[#94A3B8]">{recommendations.length} prevention suggestions — review and decide</span>
            {recommendations.length === 0 ? (
              <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-emerald-500/30 mx-auto mb-3" />
                <p className="text-[#94A3B8] text-sm">No recommendations yet. Run recurrence analysis first.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {recommendations.map(rec => (
                  <div key={rec.recommendation_id} data-testid={`rec-${rec.recommendation_id}`}
                    className="bg-[#0F1E35] border border-[#1E3A5F] rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${SEVERITY_COLORS[rec.priority]}`}>{rec.priority}</span>
                        <span className="text-xs font-bold text-white">{rec.title}</span>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${rec.status === "pending" ? "bg-amber-500/20 text-amber-400" : rec.status === "accepted" ? "bg-emerald-500/20 text-emerald-400" : rec.status === "rejected" ? "bg-red-500/20 text-red-400" : "bg-blue-500/20 text-blue-400"}`}>
                        {rec.status}
                      </span>
                    </div>
                    <p className="text-xs text-[#94A3B8] mb-1"><strong className="text-white">Rationale:</strong> {rec.rationale}</p>
                    <p className="text-xs text-[#94A3B8] mb-2"><strong className="text-white">Action:</strong> {rec.recommended_action}</p>
                    {rec.status === "pending" && (
                      <div className="flex items-center gap-2 pt-2 border-t border-[#1E3A5F]">
                        <button onClick={() => decideRecommendation(rec.recommendation_id, "accepted")} data-testid={`accept-${rec.recommendation_id}`}
                          className="px-3 py-1 bg-emerald-500 text-black text-[10px] font-bold rounded-[3px]">Accept</button>
                        <button onClick={() => decideRecommendation(rec.recommendation_id, "drafted")}
                          className="px-3 py-1 bg-blue-500/20 text-blue-400 text-[10px] font-bold rounded-[3px]">Draft Action</button>
                        <button onClick={() => decideRecommendation(rec.recommendation_id, "rejected")}
                          className="px-3 py-1 bg-red-500/20 text-red-400 text-[10px] font-bold rounded-[3px]">{t('common:actions.reject')}</button>
                        <div className="flex items-center gap-1 ml-auto">
                          {feedbackSent[rec.recommendation_id] ? <span className="text-[10px] text-emerald-400">Feedback sent</span> : (
                            <>
                              <button onClick={() => sendFeedback(rec.recommendation_id, "useful")} className="p-0.5 text-[#94A3B8] hover:text-emerald-400"><ThumbsUp className="w-3 h-3" /></button>
                              <button onClick={() => sendFeedback(rec.recommendation_id, "not_useful")} className="p-0.5 text-[#94A3B8] hover:text-red-400"><ThumbsDown className="w-3 h-3" /></button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    {rec.decision_by && <p className="text-[10px] text-[#94A3B8] mt-1">Decided by {rec.decision_by} on {new Date(rec.decision_at).toLocaleString()}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Rules Tab */}
        {tab === "rules" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#94A3B8]">{rules.length} prevention rules</span>
              <button onClick={() => setShowNewRule(true)} data-testid="add-rule-btn"
                className="flex items-center gap-1 px-3 py-1.5 bg-[#1A6BCC] text-white text-xs font-bold rounded-[3px]">
                <PlusCircle className="w-3.5 h-3.5" /> Add Rule
              </button>
            </div>
            {showNewRule && (
              <div className="bg-[#0F1E35] border border-[#1A6BCC]/50 rounded-lg p-4 space-y-3" data-testid="new-rule-form">
                <h4 className="text-sm font-bold text-[#00C8E0]">New Prevention Rule</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <label className="text-[10px] text-[#94A3B8] block mb-0.5">Name</label>
                    <input value={newRule.name} onChange={e => setNewRule(p => ({ ...p, name: e.target.value }))}
                      className="w-full px-2 py-1.5 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#94A3B8] block mb-0.5">Metric</label>
                    <select value={newRule.metric} onChange={e => setNewRule(p => ({ ...p, metric: e.target.value }))}
                      className="w-full px-2 py-1.5 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white">
                      {METRIC_OPTIONS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-[#94A3B8] block mb-0.5">Op</label>
                      <select value={newRule.operator} onChange={e => setNewRule(p => ({ ...p, operator: e.target.value }))}
                        className="w-full px-2 py-1.5 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white">
                        {[">=", ">", "<=", "<", "=="].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-[#94A3B8] block mb-0.5">Threshold</label>
                      <input type="number" value={newRule.threshold} onChange={e => setNewRule(p => ({ ...p, threshold: Number(e.target.value) }))}
                        className="w-full px-2 py-1.5 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] text-[#94A3B8] block mb-0.5">Severity</label>
                    <select value={newRule.severity} onChange={e => setNewRule(p => ({ ...p, severity: e.target.value }))}
                      className="w-full px-2 py-1.5 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white">
                      {["critical", "high", "medium", "low"].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-[#94A3B8] block mb-0.5">Scope</label>
                    <select value={newRule.scope} onChange={e => setNewRule(p => ({ ...p, scope: e.target.value }))}
                      className="w-full px-2 py-1.5 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white">
                      {["per_audit", "cross_audit", "organization"].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] text-[#94A3B8] block mb-0.5">Action</label>
                    <input value={newRule.action} onChange={e => setNewRule(p => ({ ...p, action: e.target.value }))}
                      className="w-full px-2 py-1.5 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={createRule} data-testid="save-rule-btn" className="px-4 py-1.5 bg-emerald-500 text-black text-xs font-bold rounded-[3px]">{t('common:actions.save')}</button>
                  <button onClick={() => setShowNewRule(false)} className="px-4 py-1.5 bg-[#132340] text-[#94A3B8] text-xs rounded-[3px]">{t('common:actions.cancel')}</button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              {rules.map(rule => (
                <div key={rule.rule_id} data-testid={`rule-${rule.rule_id}`}
                  className={`bg-[#0F1E35] border rounded-lg p-3 ${rule.active ? "border-[#1E3A5F]" : "border-[#1E3A5F]/40 opacity-60"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-[#94A3B8]">{rule.rule_id}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${SEVERITY_COLORS[rule.severity]}`}>{rule.severity}</span>
                      <span className="text-xs font-medium text-white">{rule.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => toggleRule(rule.rule_id, rule.active)} className="text-[#94A3B8] hover:text-white">
                        {rule.active ? <ToggleRight className="w-5 h-5 text-emerald-400" /> : <ToggleLeft className="w-5 h-5" />}
                      </button>
                      <button onClick={() => deleteRule(rule.rule_id)} className="text-[#94A3B8] hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <p className="text-xs text-[#94A3B8] mb-1">{rule.description}</p>
                  <div className="flex items-center gap-3 text-[10px]">
                    <span className="text-cyan-400">IF {rule.metric} {rule.operator} {rule.threshold}</span>
                    <span className="text-[#94A3B8]">Scope: {rule.scope}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Alerts Tab */}
        {tab === "alerts" && (
          <div className="space-y-3">
            {alerts.length === 0 ? (
              <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-emerald-500/30 mx-auto mb-3" />
                <p className="text-[#94A3B8] text-sm">No alerts triggered.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {alerts.map((alert, i) => (
                  <div key={i} data-testid={`alert-${i}`}
                    className={`bg-[#0F1E35] border rounded-lg p-3 ${alert.severity === "critical" ? "border-red-500/40" : "border-[#1E3A5F]"}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className={`w-4 h-4 ${alert.severity === "critical" ? "text-red-400" : "text-amber-400"}`} />
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${SEVERITY_COLORS[alert.severity]}`}>{alert.severity}</span>
                        <span className="text-xs font-medium text-white">{alert.rule_name}</span>
                      </div>
                    </div>
                    <div className="text-xs text-[#94A3B8]">{alert.metric} = <span className="text-white font-bold">{alert.metric_value}</span> (threshold: {alert.operator} {alert.threshold})</div>
                    {alert.action && <p className="text-xs text-amber-400 mt-1">Action: {alert.action}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

export default CAPAPreventionPage;
